import React from "react";
import { FunnelChart } from "@/features/investors/components/charts/FunnelChart";
import { HBars } from "@/features/investors/components/charts/HBars";
import { CHART_COLORS } from "@/features/investors/components/charts/palette";
import {
    CardHeader,
    InsightHtml,
    MetricText,
    Micro,
    SectionHead,
} from "@/features/investors/components/atoms/InvestorsAtoms";
import { InvestorsDashboardData } from "@/features/investors/types/investors.types";
import { InvestorsInsights } from "@/features/investors/utils/investors-insights";
import { investorsFormatters as fmt } from "@/features/investors/utils/investors-formatters";

const BREAKDOWN_COLORS = [
    CHART_COLORS.blue,
    CHART_COLORS.orange,
    CHART_COLORS.violet,
    CHART_COLORS.teal,
    CHART_COLORS.coral,
];

type Props = {
    data: InvestorsDashboardData;
    insights: InvestorsInsights;
};

export const RiskSection: React.FC<Props> = ({ data, insights }) => {
    const { risk, executive } = data;
    const created = executive.createdOperations.value;
    const completed = executive.completedOperations.value;
    const reviewRate = risk.reviewRatePct.value;
    const blockRate = risk.validationBlockRatePct.value;

    const funnelSteps =
        created !== null && completed !== null
            ? [
                  { label: "Operations created", value: created, color: CHART_COLORS.blue },
                  { label: "Completed", value: completed, color: CHART_COLORS.green },
                  {
                      label: "Flagged for review",
                      value: reviewRate !== null ? Math.round((reviewRate / 100) * created) : 0,
                      color: CHART_COLORS.gold,
                  },
                  {
                      label: "Validation blocked",
                      value: blockRate !== null ? Math.round((blockRate / 100) * created) : 0,
                      color: CHART_COLORS.coral,
                  },
              ]
            : [];

    return (
        <section className="sec" id="sec-risk">
            <SectionHead
                index="07"
                title="Risk & compliance"
                question={
                    <>
                        Question: <em>Is growth controlled?</em>
                    </>
                }
            />
            <div className="grid" style={{ gridTemplateColumns: "repeat(4,1fr)", marginBottom: 20 }}>
                <Micro
                    label="Review rate"
                    value={<MetricText metric={risk.reviewRatePct} render={(value) => fmt.pct(value, 2)} />}
                    sub="Of created operations"
                />
                <Micro
                    label="Validation blocks"
                    value={
                        <MetricText metric={risk.validationBlockRatePct} render={(value) => fmt.pct(value, 2)} />
                    }
                    sub="Operations hard-stopped at validation"
                />
                <Micro
                    label="Cleared after review"
                    value={
                        <MetricText
                            metric={risk.completionAfterReviewPct}
                            render={(value) => fmt.pct(value)}
                        />
                    }
                    valueColor="var(--pos)"
                    sub="Reviewed operations that completed"
                />
                <Micro
                    label="Revenue at risk"
                    value={<MetricText metric={risk.revenueAtRisk} render={fmt.money} />}
                    sub="Held in reviewed, blocked or in-flight ops"
                />
            </div>
            <div className="grid" style={{ gridTemplateColumns: "1.2fr 1fr" }}>
                <div className="card">
                    <CardHeader title="Demand funnel" sub="Created vs completed in the period" />
                    {funnelSteps.length > 0 ? (
                        <FunnelChart steps={funnelSteps} formatValue={fmt.numShort} showPct={false} />
                    ) : (
                        <div style={{ color: "var(--sub)", fontSize: 13 }}>No operations in this period.</div>
                    )}
                </div>
                <div className="card">
                    <CardHeader title="Validation block types" sub="Share of blocked operations" />
                    {risk.validationTypes.length > 0 ? (
                        <HBars
                            items={risk.validationTypes.map((item, index) => ({
                                label: item.label,
                                value: item.value,
                                color: BREAKDOWN_COLORS[index % BREAKDOWN_COLORS.length],
                            }))}
                            formatValue={fmt.num}
                        />
                    ) : (
                        <div style={{ color: "var(--sub)", fontSize: 13 }}>
                            No validation blocks recorded in this period — the read index does not yet
                            project block types.
                        </div>
                    )}
                </div>
            </div>
            <div style={{ marginTop: 20 }}>
                <InsightHtml html={insights.notes.risk} />
            </div>
        </section>
    );
};
